// 極簡 XML 解析器：足以讀 XMind 8 的 content.xml（元素、屬性、文字、CDATA、實體）。不處理 DTD。

export interface XmlElement {
  /** 去掉命名空間前綴的名稱 */
  name: string;
  attrs: Record<string, string>;
  children: XmlElement[];
  text: string;
}

const ENTITIES: Record<string, string> = { lt: '<', gt: '>', amp: '&', quot: '"', apos: "'" };

// 解碼 XML 實體。
function decodeEntities(text: string): string {
  return text.replace(/&(#x[0-9a-f]+|#\d+|\w+);/gi, (match, entity: string) => {
    if (entity[0] === '#') {
      const code = entity[1] === 'x' || entity[1] === 'X' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10);
      return Number.isFinite(code) ? String.fromCodePoint(code) : match;
    }
    return ENTITIES[entity] ?? match;
  });
}

const localName = (name: string) => name.slice(name.indexOf(':') + 1);

// 解析 XML 字串，回傳根元素。格式錯誤時丟出錯誤。
export function parseXml(source: string): XmlElement {
  const root: XmlElement = { name: '#document', attrs: {}, children: [], text: '' };
  const stack: XmlElement[] = [root];
  let position = 0;
  while (position < source.length) {
    const next = source.indexOf('<', position);
    if (next < 0) { stack[stack.length - 1]!.text += decodeEntities(source.slice(position)); break; }
    if (next > position) stack[stack.length - 1]!.text += decodeEntities(source.slice(position, next));
    if (source.startsWith('<!--', next)) { position = source.indexOf('-->', next) + 3; if (position < 3) throw new Error('XML 註解未結束'); continue; }
    if (source.startsWith('<![CDATA[', next)) {
      const close = source.indexOf(']]>', next);
      if (close < 0) throw new Error('XML CDATA 未結束');
      stack[stack.length - 1]!.text += source.slice(next + 9, close);
      position = close + 3;
      continue;
    }
    if (source.startsWith('<?', next) || source.startsWith('<!', next)) { position = source.indexOf('>', next) + 1; continue; }
    const close = source.indexOf('>', next);
    if (close < 0) throw new Error('XML 標籤未結束');
    const tag = source.slice(next + 1, close);
    position = close + 1;
    if (tag.startsWith('/')) {
      const name = localName(tag.slice(1).trim());
      const current = stack.pop();
      if (!current || current.name !== name) throw new Error(`XML 標籤不對稱：</${name}>`);
      continue;
    }
    const selfClosing = tag.endsWith('/');
    const body = selfClosing ? tag.slice(0, -1) : tag;
    const nameMatch = /^\s*([^\s/>]+)/.exec(body);
    if (!nameMatch) throw new Error('XML 標籤缺少名稱');
    const element: XmlElement = { name: localName(nameMatch[1]!), attrs: {}, children: [], text: '' };
    for (const attr of body.slice(nameMatch[0].length).matchAll(/([^\s=]+)\s*=\s*("([^"]*)"|'([^']*)')/g)) {
      element.attrs[localName(attr[1]!)] = decodeEntities(attr[3] ?? attr[4] ?? '');
    }
    stack[stack.length - 1]!.children.push(element);
    if (!selfClosing) stack.push(element);
  }
  if (stack.length !== 1) throw new Error(`XML 未關閉的標籤：<${stack[stack.length - 1]!.name}>`);
  const top = root.children[0];
  if (!top) throw new Error('XML 沒有根元素');
  return top;
}

// 取第一個指定名稱的子元素。
export function child(element: XmlElement | undefined, name: string): XmlElement | undefined {
  return element?.children.find(item => item.name === name);
}

// 取所有指定名稱的子元素。
export function childrenNamed(element: XmlElement | undefined, name: string): XmlElement[] {
  return element?.children.filter(item => item.name === name) ?? [];
}
